import type { ErrorInfo, ReactNode } from 'react';
import { Component } from 'react';

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = {
    error: null,
  };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Unhandled client render error', error, errorInfo);
  }

  private handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.error == null) {
      return this.props.children;
    }

    return (
      <div className="dark flex min-h-screen items-center justify-center bg-[radial-gradient(circle_at_top,rgba(59,130,246,0.18),transparent_32%),linear-gradient(180deg,#0b0b0b_0%,#060606_100%)] px-4 py-8">
        <div className="w-full max-w-3xl overflow-hidden rounded-2xl border border-white/[.08] bg-black/55 shadow-[0_20px_80px_rgba(0,0,0,0.45)] backdrop-blur-md">
          <div className="border-b border-white/[.06] px-6 py-5">
            <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-red-300/80">
              Application Error
            </span>
            <h1 className="mt-3 font-sans text-2xl font-semibold tracking-tight text-neutral-100">
              MeridianView AI hit an unexpected UI error
            </h1>
            <p className="mt-2 max-w-2xl font-sans text-[14px] leading-6 text-neutral-400">
              The app can&apos;t continue rendering this screen. Reload the page to remount the app
              and recover the session.
            </p>
          </div>

          <div className="grid gap-5 px-6 py-6">
            {import.meta.env.DEV && (
              <div className="rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 font-mono text-[11px] leading-5 text-red-200">
                {this.state.error.message}
              </div>
            )}

            <div className="flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={this.handleReload}
                className="inline-flex h-10 items-center justify-center rounded-md border border-white/[.08] bg-white/[.04] px-4 font-mono text-[11px] font-semibold tracking-[0.08em] text-neutral-300 transition-all duration-150 hover:bg-white/[.08] hover:text-neutral-100"
              >
                Reload Page
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }
}
